import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect,
  memo,
} from 'react';
import { path, intersperse, prepend, init, view } from 'ramda';
import { createPortal } from 'react-dom';
import {
  IClientAssociationModel,
  IClientBusinessModel,
} from '../../../../../../Types/ClientModelTypes';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import {
  IMasterCompProps,
  TSelectedDetail,
} from '../../../../../../Types/MasterDetailTypes';
import defaultColumns from '../../../Utils/defaultColumns';
import NodeQuery from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import BusinessDataDetailHeader from '../BusinessDataDetailHeader/BusinessDataDetailHeader';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import { IAssociationListConfig } from './AssociationList';
import { BreadcrumbsContext } from '../../RutinBreadcrumbs/BreadcrumbsContext';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import {
  sortRows,
  ISortInfo,
  standardRutinGetSortValue,
  standardRutinCompare,
  ISortProperty,
} from '../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { useIsExtraSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { BusinessCollectionMenuSmall } from '../BusinessCollectionMenu/BusinessCollectionMenuSmall';
import {
  VisningsTypContext,
  VisningsTypContextProvider,
} from '../VisningsTypContext/VisningsTypContext';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import AssociationListMasterView from './AssociationListMasterView';
import { IUIUrval } from '../../../../../../Types/UrvalTypes';
import { addAllDataDependencies } from '../../../Const/BusinessConfigs';
import {
  NewId,
  selectedId,
} from '../../../../../../Components/MasterDetail/MasterDetailUtils';
import BusinessCollectionMenuButton from '../BusinessCollectionMenu/BusinessCollectionMenuButton';
import { CaptionContext } from '../Hocs/addCaption';
import { getEmptyUrval } from '../../../../../../Components/Urval/Utils/urvalUtils';
import { useBusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';
import {
  IFormData,
  parseText,
} from '../BusinessDataForm/Utils/BusinessDataFormUtils';
import { ThemeProvider, StyledEngineProvider } from '@mui/material';
import { AvenyThemeDark } from '../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { rutinKeys } from '../../../Const/RutinInfo';
import { getInitialSortInfo } from './AssociationListMasterUtils';
import { IFilterAttribute } from '../../../Const/Delsystem';
import { FormUpdateAssociatonType } from '../BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { filterData } from '../../ListFilteringComponent/Utils/listFilterComponentUtils';
import {
  composeLensDefs,
  LensDef,
  propsLens,
} from '../../../../../../Utils/lensUtils';
import { GridEventListener } from '@mui/x-data-grid-pro';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';

interface IOuterViewProps {
  associatedCollection: IClientBusinessModel;
  columns: readonly IColumnDef[];
  setColumns: (newColumns: readonly IColumnDef[]) => void;
  data: ICollectionDataType[] | null;
  refetch: () => void;
  loading: boolean;
  selectDetail: (id: TSelectedDetail) => void;
  selectedId: TSelectedDetail;
  inlineDetail?: React.ReactNode;
  setSelectDetailByIndex?: (
    selectDetailByIndex: (index: number) => void
  ) => void;
  onRowClick?: GridEventListener<'rowClick'>;
  selectionGrid?: boolean;
  detailHeader?: Element;
  config: IAssociationListConfig;
  baseCollection: IClientBusinessModel;
  baseId: string | null;
  detaljurval: IUIUrval | null;
  setDetaljurval: (newUrval: IUIUrval | null) => void;
  association: IClientAssociationModel | undefined;
  associationPath?: string;
  ownId: string | null;
  collectionName: string;
  editable?: boolean;
  searchText?: string;
  setSearchText?: (newValue: string) => void;
  filterAttributes?: IFilterAttribute[];
}

interface IInnerViewProps extends IOuterViewProps {
  newItems: ICollectionDataType[] | undefined;
  onAddNewItem?: (newItem: ICollectionDataType) => void;
  onDeleteItem?: (item: ICollectionDataType) => void;
}

const AssociationListMaster = (props: IInnerViewProps) => {
  const { selectDetail } = props;
  const { visningsTyp, rutinId } = useContext(VisningsTypContext);
  const modelContext = useContext(ModelContext);
  const { menuRef } = useContext(CaptionContext);
  const rutinKod = rutinKeys[rutinId];

  const [sortProperties, setSortProperties] = useState<ISortProperty[]>(() => {
    return getInitialSortInfo(
      props.associatedCollection.CollectionName,
      props.columns,
      modelContext,
      props.data
    ).properties;
  });

  const updateSortInfo = (info: ISortInfo<ICollectionDataType>) => {
    setSortProperties(info.properties);
  };

  const sortedRows = useMemo(() => {
    const normalizedRows = (props.data || []).map(row => ({
      ...row,
      _Associations: row._Associations ?? null,
    }));

    return sortRows(
      sortProperties,
      normalizedRows,
      standardRutinGetSortValue(props.associatedCollection.CollectionName),
      standardRutinCompare
    );
  }, [props.data, sortProperties, props.associatedCollection.CollectionName]);

  const sortInfo: ISortInfo<ICollectionDataType> = useMemo(
    () => ({ rows: sortedRows, properties: sortProperties }),
    [sortedRows, sortProperties]
  );

  const currentIndex = useMemo(
    () =>
      sortInfo.rows.findIndex(
        data => data?.id === selectedId(props.selectedId)
      ),
    [sortInfo.rows, props.selectedId]
  );
  const next = useMemo(() => {
    if (!sortInfo.rows || currentIndex === null) {
      return undefined;
    }
    const newId = sortInfo.rows[currentIndex + 1]?.id;
    const newData = sortInfo.rows[currentIndex + 1];
    const newNextId = sortInfo.rows[currentIndex + 2]?.id;
    const newPrevId = sortInfo.rows[currentIndex]?.id;
    if (!newId) {
      return undefined;
    }
    return () => {
      props.selectDetail({
        id: newId,
        data: newData,
        nextId: newNextId,
        prevId: newPrevId,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortInfo.rows, currentIndex]);

  const prev = useMemo(() => {
    if (!sortInfo.rows || currentIndex === null) {
      return undefined;
    }
    const newId = sortInfo.rows[currentIndex - 1]?.id;
    const newData = sortInfo.rows[currentIndex - 1];
    const newNextId = sortInfo.rows[currentIndex]?.id;
    const newPrevId = sortInfo.rows[currentIndex - 2]?.id;
    if (!newId) {
      return undefined;
    }
    return () => {
      props.selectDetail({
        id: newId,
        data: newData,
        nextId: newNextId,
        prevId: newPrevId,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortInfo.rows, currentIndex]);

  const unselectDetail = useCallback(() => selectDetail(null), [selectDetail]);

  const selectNewDetail = useCallback(
    (customDetailData?: unknown) =>
      selectDetail({ id: NewId, customDetailData: customDetailData }),
    [selectDetail]
  );

  const isExtraSmall = useIsExtraSmallScreen();

  const hasUrval = Boolean(props.detaljurval);
  const filteredSortInfo = useMemo(
    () =>
      props.filterAttributes
        ? filterData(
            props.collectionName,
            sortInfo,
            props.searchText ?? '',
            props.filterAttributes,
            hasUrval,
            props.loading
          )
        : undefined,
    [
      sortInfo,
      props.searchText,
      props.filterAttributes,
      hasUrval,
      props.loading,
      props.collectionName,
    ]
  );

  const {
    headerReferences,
    topNavLevel,
    currentNavLevel,
    backButtonReferences,
    stepperReferences,
    stepperSmallRef,
    registerActiveNavLevel,
    unregisterActiveNavLevel,
  } = useContext(BreadcrumbsContext);

  const headerRef =
    props.selectedId && currentNavLevel !== undefined
      ? headerReferences[currentNavLevel]
      : null;

  const backRef =
    props.selectedId &&
    currentNavLevel !== undefined &&
    currentNavLevel === topNavLevel &&
    !props.selectionGrid
      ? backButtonReferences[currentNavLevel]
      : null;

  const stepperRef = isExtraSmall
    ? stepperSmallRef
    : props.selectedId &&
      currentNavLevel !== undefined &&
      currentNavLevel === topNavLevel
    ? stepperReferences[currentNavLevel]
    : null;

  const renderHeader =
    headerRef && visningsTyp !== 'expanderbar lista' && !props.selectionGrid;
  useEffect(() => {
    if (renderHeader && currentNavLevel) {
      registerActiveNavLevel(currentNavLevel);
      return () => unregisterActiveNavLevel(currentNavLevel);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderHeader, currentNavLevel]);

  const detaljUrvalInfo = props.association
    ? {
        associationName: props.association?.AssociationName ?? '',
        id: props.ownId || '',
        collectionName: props.collectionName,
      }
    : undefined;

  return (
    <EDBox
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: isExtraSmall ? 'column' : 'row',
      }}
    >
      <AssociationListMasterView
        collection={props.associatedCollection}
        association={props.association}
        associationPath={props.associationPath}
        columns={props.columns}
        config={props.config}
        loading={props.loading}
        editable={props.editable && props.config.integrated}
        refetch={props.refetch}
        selectDetail={props.selectDetail}
        selectNewDetail={selectNewDetail}
        selectedId={props.selectedId}
        selectionGrid={props.selectionGrid}
        setColumns={props.setColumns}
        inlineDetail={props.inlineDetail}
        autoHeight={props.config.autoHeight ?? Boolean(props.config.inCard)}
        sortInfo={{
          ...sortInfo,
          rows:
            props.newItems && !props.config.integrated
              ? [...props.newItems, ...sortInfo.rows]
              : sortInfo.rows,
        }}
        newItems={props.newItems}
        updateSortInfo={updateSortInfo}
        isSmall={isExtraSmall}
        baseCollection={props.baseCollection}
        baseId={props.baseId}
        urval={props.detaljurval}
        setUrval={props.setDetaljurval}
        urvalInfo={detaljUrvalInfo}
        numberOfRows={props.data?.length}
        onAddNewItem={props.onAddNewItem}
        onDeleteItem={props.onDeleteItem}
        onRowClick={props.onRowClick}
        rutinId={rutinId}
        filteredSortInfo={filteredSortInfo}
        searchText={props.searchText}
        setSearchText={props.setSearchText}
        filterAttributes={props.filterAttributes}
      />
      {renderHeader && headerRef && (
        <BusinessDataDetailHeader
          caption={
            props.config.customSOLabel ||
            props.associatedCollection.RootType.SOLabel
          }
          collection={props.associatedCollection}
          columns={props.columns}
          data={props.data}
          selectDetail={props.selectDetail}
          selectedId={props.selectedId}
          currentIndex={currentIndex}
          next={next}
          prev={prev}
          currentlyOpen={Boolean(
            props.selectedId &&
              currentNavLevel === topNavLevel &&
              !props.selectionGrid
          )}
          headerRef={headerRef}
          stepperRef={stepperRef}
          rutinKod={rutinKod}
        />
      )}
      {menuRef &&
        menuRef.current !== null &&
        createPortal(
          <BusinessCollectionMenuButton
            baseId={props.baseId}
            detaljurvalInfo={detaljUrvalInfo}
            baseCollection={props.baseCollection}
            collection={props.associatedCollection}
            columns={props.columns}
            setColumns={props.setColumns}
            urval={props.detaljurval}
            setUrval={props.setDetaljurval}
            sortedData={sortInfo}
            updateSortOrder={updateSortInfo}
            refetch={props.refetch}
            disableCreateNew
            inCard={Boolean(props.config.inCard)}
            onAddNewItem={props.onAddNewItem}
            rutinId={rutinId}
            datasetLength={sortInfo.rows.length}
            rows={sortInfo.rows}
          />,
          menuRef.current
        )}
      {backRef &&
        visningsTyp !== 'expanderbar lista' &&
        createPortal(
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={AvenyThemeDark}>
              <EDIconButton
                icon="arrow_back"
                color={'inherit'}
                tooltip={`Tillbaka till ${props.associatedCollection.RootType.SBLabel}`}
                padding="dense"
                marginTop={0.5}
                marginBottom={0.5}
                onClick={unselectDetail}
              />
            </ThemeProvider>
          </StyledEngineProvider>,
          backRef
        )}
      {!props.config.inCard && isExtraSmall && (
        <BusinessCollectionMenuSmall
          collection={props.associatedCollection}
          columns={props.columns}
          setColumns={props.setColumns}
          urval={null}
          setUrval={null}
          refetch={props.refetch}
          sortedData={sortInfo}
          updateSortOrder={updateSortInfo}
          baseCollection={props.baseCollection}
          baseId={props.baseId}
          isAssociation
          datasetLength={sortInfo.rows.length}
          rows={sortInfo.rows}
        />
      )}
    </EDBox>
  );
};

export const EnhancedAssociationListMaster = memo(AssociationListMaster);

const MasterComp = (
  association: IClientAssociationModel,
  collection: IClientBusinessModel,
  associatedCollection: IClientBusinessModel,
  ownId: string | null,
  config: IAssociationListConfig,
  associationPath?: string,
  rutinKod?: string
): React.ComponentType<React.PropsWithChildren<IMasterCompProps>> => {
  const id = ownId === NewId ? '' : ownId;
  return (props: IMasterCompProps) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const businessDataFormContext = useBusinessDataFormContext();
    const update = businessDataFormContext?.update;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formContextPath = useMemo(
      () =>
        associationPath
          ? prepend(
              '_Associations',
              intersperse<LensDef>(
                '_Associations',
                associationPath
                  .split('.')
                  .map(pathPart => propsLens(pathPart, pathPart + '_All'))
              )
            )
          : undefined,
      []
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formValuePath = useMemo(
      () =>
        associationPath
          ? associationPath.split('.').length === 1
            ? []
            : prepend(
                '_Associations',
                intersperse(
                  '_Associations',
                  init(associationPath.split('.')).map(x => `${x}_Values`)
                )
              )
          : undefined,
      []
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    let newItems = useMemo(
      () =>
        formValuePath &&
        path<ICollectionDataType[]>(
          [
            ...formValuePath,
            '_Associations',
            `${association.AssociationName}_Add_Values`,
          ],
          businessDataFormContext?.formState.values
        ),
      [businessDataFormContext?.formState.values, formValuePath]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const deletedItems = useMemo(
      () =>
        formValuePath &&
        path<ICollectionDataType[]>(
          [
            ...formValuePath,
            '_Associations',
            `${association.AssociationName}_Delete_Values`,
          ],
          businessDataFormContext?.formState.values
        ),
      [businessDataFormContext?.formState.values, formValuePath]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formContextData = useMemo(() => {
      const data = formContextPath
        ? view<IFormData, ICollectionDataType[]>(
            composeLensDefs(...formContextPath),
            parseText(businessDataFormContext?.data ?? {})
          )
        : null;
      return data
        ? data.filter(x => !deletedItems?.find(deleted => deleted.id === x.id))
        : null;
    }, [businessDataFormContext?.data, deletedItems, formContextPath]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formContextCurrentValues = useMemo(
      () =>
        formContextPath
          ? view<any, ICollectionDataType[]>(
              composeLensDefs(...formContextPath),
              businessDataFormContext?.formState.currentValues
            )
          : null,
      [businessDataFormContext?.formState.currentValues, formContextPath]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    newItems = useMemo(
      () =>
        newItems &&
        newItems?.filter(
          newItem => !deletedItems?.find(deleted => deleted.id === newItem.id)
        ),
      [deletedItems, newItems]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const onAddNewItem = useCallback(
      (newItem: ICollectionDataType | ICollectionDataType[]) => {
        const updateArray = Array.isArray(newItem)
          ? newItem.map(
              item =>
                [
                  ...init(associationPath?.split('.') ?? []),
                  {
                    operation: 'Add_Values',
                    associationName: association.AssociationName,
                    id: item.id,
                    values: item,
                  },
                ] as FormUpdateAssociatonType[]
            )
          : ([
              ...init(associationPath?.split('.') ?? []),
              {
                operation: 'Add_Values',
                associationName: association.AssociationName,
                id: newItem.id,
                values: newItem,
              },
            ] as FormUpdateAssociatonType[]);
        update?.(updateArray);
      },
      [update]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const onDeleteItem = useCallback(
      (item: ICollectionDataType) => {
        update?.([
          ...init(associationPath?.split('.') ?? []),
          {
            operation: 'Delete_Values',
            associationName: association.AssociationName,
            id: item.id,
          },
        ]);
      },
      [update]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [urval, setUrval] = useState<IUIUrval | null>(getEmptyUrval());
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { rutinId } = useContext(VisningsTypContext);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [columns, setColumns] = useState(
      config.customDefaultColumns ||
        defaultColumns(associatedCollection.CollectionName, rutinKod)
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const attributeDefs = useMemo(
      () =>
        addAllDataDependencies(
          associatedCollection.CollectionName,
          columns,
          association.AssociationName
        ),
      [columns]
    );
    return (
      <NodeQuery
        collectionName={collection.CollectionName}
        id={id}
        fieldDefs={attributeDefs}
        detaljurval={{
          associationName: association.AssociationName,
          urval: urval,
        }}
        associatedCollection={associatedCollection}
        skip={
          businessDataFormContext?.formState.loading ||
          formContextCurrentValues != null ||
          formContextData != null
        }
        disableAddRefetchButtonQuery={config.disableAddRefetchButtonQuery}
      >
        {(loading, node, refetch) => (
          <VisningsTypContextProvider
            rutinID={rutinId}
            collectionName={associatedCollection.CollectionName}
            inCard={config.inCard}
            defaultVisningstyp={config.defaultVisningstyp}
          >
            <EnhancedAssociationListMaster
              associatedCollection={associatedCollection}
              association={association}
              associationPath={associationPath}
              ownId={id}
              collectionName={collection.CollectionName}
              columns={columns}
              setColumns={setColumns}
              data={
                config?.integrated
                  ? formContextCurrentValues ?? null
                  : formContextData
                  ? formContextData ?? []
                  : node &&
                    (path(
                      ['_Associations', association.AssociationName],
                      parseText(node)
                    ) ||
                      null)
              }
              newItems={newItems}
              loading={loading}
              refetch={refetch}
              selectDetail={props.selectDetail}
              inlineDetail={props.inlineDetail}
              selectedId={props.selectedDetail}
              selectionGrid={props.selectionGrid}
              config={config}
              baseCollection={collection}
              baseId={id}
              detaljurval={urval}
              setDetaljurval={setUrval}
              onAddNewItem={onAddNewItem}
              onDeleteItem={config.showDeleteIcon ? onDeleteItem : undefined}
              onRowClick={props.onRowClick}
              editable={
                config.integrated && businessDataFormContext?.formState.editMode
              }
            />
          </VisningsTypContextProvider>
        )}
      </NodeQuery>
    );
  };
};

export default MasterComp;
